import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import SettingEditor from './standards/SettingEditor';
import TagsEditor from './standards/TagsEditor';
import ChangeLog from './standards/ChangeLog';
import { SettingRow, StandardsData } from './standards/types';

type Section = 'limits' | 'tags' | 'log';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'limits', label: 'Limits' },
  { id: 'tags', label: 'Worklog tags' },
  { id: 'log', label: 'Change log' },
];

const GROUPS: { area: SettingRow['area']; title: string; note: string }[] = [
  { area: 'planning', title: 'Planning', note: 'Used by the Planning page and the Compliance overview. The Jira conventions start day sets where "Before" begins on every page that picks a period.' },
  { area: 'effort', title: 'Time logging', note: 'Used by the Time logging page, My audit and the Compliance overview.' },
];

// Settings → Standards: the limits and lists the pages judge by. A change applies to every month,
// past included, because the reports are live; the change log records who changed what and when.
export default function StandardsTab() {
  const [section, setSection] = useState<Section>('limits');
  const [data, setData] = useState<StandardsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logKey, setLogKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getStandards();
      if (r.success) {
        setData(r.data);
        setError(null);
      } else setError(r.error || 'Could not load the standards');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load the standards');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const changed = () => {
    setLogKey((n) => n + 1);
    void load();
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Standards</h3>
        <p className="text-sm text-gray-600">The limits and lists the pages judge by. A change applies to every month, past included.</p>
      </div>

      <div role="tablist" aria-label="Standards settings" className="inline-flex rounded-xl bg-gray-100 p-1">
        {SECTIONS.map((s) => {
          const on = section === s.id;
          return (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setSection(s.id)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-[background-color,color,box-shadow] duration-150 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                on ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {error && <div className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

      {section === 'limits' && (
        <div className="space-y-6">
          {!data && !error ? (
            <div className="h-48 animate-pulse rounded-2xl bg-gray-100 motion-reduce:animate-none" role="status" aria-label="Loading the standards" />
          ) : (
            GROUPS.map((g) => (
              <section key={g.area} className="rounded-2xl bg-white px-5 pt-5 shadow-sm ring-1 ring-gray-200/70" aria-label={g.title}>
                <h3 className="text-sm font-semibold text-gray-900">{g.title}</h3>
                <p className="mt-0.5 text-xs text-gray-500">{g.note}</p>
                <div className="mt-1 divide-y divide-gray-100">
                  {(data?.settings ?? []).filter((s) => s.area === g.area).map((s) => (
                    <SettingEditor key={`${s.key}:${s.value}`} setting={s} onSaved={changed} />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}
      {section === 'tags' && data && <TagsEditor tags={data.tags} aliases={data.aliases} onChanged={changed} />}
      {section === 'log' && <ChangeLog refreshKey={logKey} />}
    </div>
  );
}
